import { apiClient, getApiBaseUrl, tokenStorage } from "./client";
import type { CallLimit, Company, SessionType } from "./types";

/** Per-period bucket — calls/outcomes/talk/reach for one window
 *  (today, week, month, or quarter). Mirrors the backend's
 *  `CallPeriodStats` schema. */
export interface CallPeriodStats {
  calls: number;
  interested: number;
  neutral: number;
  not_interested: number;
  no_answer: number;
  talk_minutes: number;
  reach_rate: number;
}

/** Aggregated call stats for the Stats tab. The backend always
 *  returns every bucket regardless of the `period` query param —
 *  the UI picks the active one. Top-level `total_calls / interested
 *  / talk_minutes / reach_rate` mirror the month bucket and are
 *  kept for backward compatibility. */
export interface CallStatsResponse {
  today: CallPeriodStats;
  week: CallPeriodStats;
  month: CallPeriodStats;
  quarter: CallPeriodStats;
  streak: number;
  personal_record: { calls: number; date: string } | null;
  badges?: Array<{ key: string; icon: string; label: string }>;
  total_calls: number;
  interested: number;
  talk_minutes: number;
  reach_rate: number;
}

export interface CallDailyResponse {
  daily: Array<{
    date: string;
    calls: number;
    interested: number;
    no_answer: number;
    talk_minutes: number;
  }>;
  weekly_rates: Array<{
    week: string;
    interest_rate: number;
    reach_rate: number;
  }>;
  hourly: Array<{ hour: number; calls: number; interested: number }>;
}

export type CallStatsPeriod = "today" | "week" | "month" | "quarter";
/** Daily / weekly / hourly rollups only exist for windows ≥ 7 days —
 *  the backend rejects `today` here. */
export type CallDailyPeriod = "week" | "month" | "quarter";

/** Call outcomes the History tab can filter on — the four the
 *  Call Center session logs. Mirrors `CallOutcome` minus the
 *  client-only `skipped` / `refused` (those never write a
 *  `call_logged` activity). */
export type CallHistoryOutcome =
  | "interested"
  | "neutral"
  | "not_interested"
  | "no_answer";

/** Time window the History tab aggregates over. The backend resolves
 *  the concrete date range from `period` plus the reference `date`
 *  (any calendar date inside the wanted window). */
export type CallHistoryPeriod = "day" | "week" | "month" | "year";

/** One logged call as shown in the History table. `company_id`
 *  drives the link to the company detail page. */
export interface CallHistoryEntry {
  id: number;
  created_at: string;
  company_id: number;
  company_name: string;
  contact_name: string | null;
  contact_phone: string | null;
  /** Withheld from this reader (see `Company.hidden_details` in types.ts). */
  hidden_details?: string[];
  outcome: string | null;
  notes: string | null;
}

/** Outcome breakdown for the selected day — drives the recap row
 *  under the History table. `calls` equals the response `total`. */
export interface CallHistorySummary {
  calls: number;
  interested: number;
  neutral: number;
  not_interested: number;
  no_answer: number;
}

/** Paginated `call-history` payload — newest call first. */
export interface CallHistoryResponse {
  items: CallHistoryEntry[];
  total: number;
  page: number;
  page_size: number;
  summary: CallHistorySummary;
}

/** Fetch the aggregated call stats for the Stats tab. `userId` scopes
 *  the numbers to one operator (`"all"` for the whole team); omit it
 *  and the backend pins the response to the current user — only
 *  callers with `call_center.view_team` should pass it. Returns null
 *  when the backend doesn't ship the endpoint yet (404 / 501); the
 *  callers should fall back to a "no data yet" empty state. */
export async function getCallStats(
  slug: string,
  period: CallStatsPeriod,
  userId?: string,
): Promise<CallStatsResponse | null> {
  try {
    const res = await apiClient.get<CallStatsResponse>(
      `/projects/${slug}/analytics/call-stats`,
      { params: { period, user_id: userId || undefined } },
    );
    return res.data;
  } catch {
    return null;
  }
}

/** Daily / weekly / hourly rollups for the Stats tab charts. `userId`
 *  scopes them to one operator (`"all"` for the team) — see
 *  `getCallStats` for the gating rule. */
export async function getCallDaily(
  slug: string,
  period: CallDailyPeriod,
  userId?: string,
): Promise<CallDailyResponse | null> {
  try {
    const res = await apiClient.get<CallDailyResponse>(
      `/projects/${slug}/analytics/call-daily`,
      { params: { period, user_id: userId || undefined } },
    );
    return res.data;
  } catch {
    return null;
  }
}

/** Fetch the paginated call log for one window. `period` picks the
 *  window size (day / week / month / year) and `date` is any
 *  `YYYY-MM-DD` calendar date inside it — the backend resolves the
 *  concrete range. `search` and `outcome` are optional filters.
 *  `userId` scopes the log to one operator (`"all"` for the team);
 *  omit it and the backend pins the response to the current user —
 *  only callers with `call_center.view_team` should pass it. Returns
 *  null when the backend doesn't ship the endpoint yet (404 / 501);
 *  the caller falls back to an empty state. */
export async function getCallHistory(
  slug: string,
  params: {
    period: CallHistoryPeriod;
    date: string;
    search?: string;
    outcome?: CallHistoryOutcome | null;
    userId?: string;
    page?: number;
    page_size?: number;
  },
): Promise<CallHistoryResponse | null> {
  try {
    const res = await apiClient.get<CallHistoryResponse>(
      `/projects/${slug}/analytics/call-history`,
      {
        params: {
          period: params.period,
          date: params.date,
          search: params.search?.trim() || undefined,
          outcome: params.outcome || undefined,
          user_id: params.userId || undefined,
          page: params.page ?? 1,
          page_size: params.page_size ?? 50,
        },
      },
    );
    return res.data;
  } catch {
    return null;
  }
}

/** Leads handed to one operator at session start, already locked to
 *  them server-side so a parallel operator can't be served the same
 *  rows. Shape mirrors the `listCompanies` items the queue used to be
 *  built from. */
export interface StartCallSessionResponse {
  leads: Company[];
  /** Present only for a salesperson with a call limit. */
  call_limit?: CallLimit | null;
}

/** The reader's own allowance of numbers (`limit` null when unlimited). */
export async function getCallLimit(slug: string): Promise<CallLimit> {
  const res = await apiClient.get<CallLimit>(`/projects/${slug}/call-center/limit`);
  return res.data;
}

/** Open a call session. The backend resolves the bucket from
 *  `session_type`, reserves up to `count` leads to the current user,
 *  and returns them. Replaces the old `listCompanies(session_type=…)`
 *  fetch — same selection, but the rows come back locked so two
 *  operators dialing at once never collide on the same lead. */
export async function startCallSession(
  slug: string,
  params: { session_type: SessionType; count: number },
): Promise<StartCallSessionResponse> {
  const res = await apiClient.post<StartCallSessionResponse>(
    `/projects/${slug}/call-center/session/start`,
    { session_type: params.session_type, count: params.count },
  );
  return res.data;
}

/** Release every lead still locked to the current operator — the ones
 *  they skipped or never reached. Processed leads free themselves when
 *  their pipeline status changes, so this is a no-op for a fully-worked
 *  queue. Idempotent: safe to call more than once. */
export async function endCallSession(slug: string): Promise<void> {
  await apiClient.post(`/projects/${slug}/call-center/session/end`);
}

/** Fire-and-forget lock release for page teardown (tab close, hard
 *  navigation). `navigator.sendBeacon` can't attach the `Authorization`
 *  header this API authenticates with (Bearer token, not a cookie), so
 *  we use `fetch` with `keepalive` instead — it survives the unload AND
 *  carries the token. Best-effort: a dropped request just leaves a lock
 *  for the backend to reap on its own TTL. */
export function endCallSessionBeacon(slug: string): void {
  const token = tokenStorage.getAccess();
  try {
    void fetch(
      `${getApiBaseUrl()}/projects/${slug}/call-center/session/end`,
      {
        method: "POST",
        keepalive: true,
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      },
    );
  } catch {
    // Best effort during teardown — nothing actionable if it throws.
  }
}
